import { loginToBEFin } from 'api/v2/users/beFinances';
import { Button } from 'components/atoms/Button';
import { Title } from 'components/atoms/Title';
import TransferDataIntervatLottie from 'components/molecules/TransferDataIntervat';
import { SizesEnum } from 'components/atoms/Title/Title.enum';
import RadioButton from 'components/atoms/Form/RadioButton/RadioButton.component';
import { useIntervatAccountantPermission } from 'hooks/Taxes/useIntervatAccountantPermission';
import useGetCustomNavigationLink from 'hooks/useGetCustomNavigationLink';
import { getBEFinSessionQueryKey } from 'hooks/useBEFinSession/getBEFinSessionQueryKey';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useGrantIntervatAccessToWorkspace from 'hooks/useGrantIntervatAccessToWorkspace';
import type { IWorkspace } from 'types/workspace.types';
import { WorkspaceMemberRole } from 'types/workspace.types';
import { getInitialColorClass } from 'utils/avatarInitialColor';
import { InformationCircleIcon } from '@heroicons/react/outline';
import { Loader } from 'components/atoms/Loader';
import useQueryClient from 'hooks/react-query/useClientQuery';
import useMutation from 'hooks/react-query/useMutation';
import { logAmplitude } from 'utils/amplitude';
import { WEBAPP_TAXESINTERVATMODAL_CLICKEDGIVEPERMISSIONAFTERWORKSPACESELECTED } from 'utils/amplitude/events/taxes.amplitude';

/** Source param for return-from-SPF detection (matches integrations pattern). */
export const INTERVAT_GRANT_SOURCE = 'intervat_grant';

export type IntervatGrantEntrySource = 'taxes_banner' | 'data_access';

interface IntervatGrantModalProps {
  onClose: () => void;
  /** When opening from Data Access for a specific accountant, pre-select this workspace. */
  initialWorkspaceId?: string;
  /** Modal entry point for Amplitude (Taxes banner vs Data Access). */
  intervatGrantEntrySource: IntervatGrantEntrySource;
}

function getAccountantDisplayName(workspace: IWorkspace): string {
  const owner =
    workspace.members?.find((m) => m.role === WorkspaceMemberRole.OWNER) ??
    workspace.members?.[0];
  if (owner) {
    return (
      [owner.first_name, owner.last_name].filter(Boolean).join(' ') ||
      workspace.name
    );
  }
  return workspace.name || workspace.organization_name || '';
}

function getWorkspaceSubline(workspace: IWorkspace): string {
  const parts = [
    workspace.organization_name || workspace.name,
    workspace.email,
  ].filter(Boolean);
  return parts.join(' · ');
}

type Step = 'workspace' | 'redirect';

const IntervatGrantModal: FC<IntervatGrantModalProps> = ({
  onClose,
  initialWorkspaceId,
  intervatGrantEntrySource,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const getRedirectUrl = useGetCustomNavigationLink();
  const { accountantWorkspaces, isLoading, canGrantIntervatWithoutRedirect } =
    useIntervatAccountantPermission({
      skipWorkspaceFetch: Boolean(initialWorkspaceId),
    });
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    initialWorkspaceId ?? null,
  );
  const [step, setStep] = useState<Step>(
    initialWorkspaceId ? 'redirect' : 'workspace',
  );
  const canGrantIntervatWithoutRedirectRef = useRef(
    canGrantIntervatWithoutRedirect,
  );

  useEffect(() => {
    if (accountantWorkspaces.length === 1 && !selectedWorkspaceId) {
      setSelectedWorkspaceId(accountantWorkspaces[0]._id);
    }
  }, [accountantWorkspaces, selectedWorkspaceId]);

  useEffect(() => {
    canGrantIntervatWithoutRedirectRef.current =
      canGrantIntervatWithoutRedirect;
  }, [canGrantIntervatWithoutRedirect]);

  const grantAccessToWorkspaceMutation = useGrantIntervatAccessToWorkspace({
    onAfterSuccess: () => onClose(),
  });
  const { mutate: grantAccessToWorkspace } = grantAccessToWorkspaceMutation;

  const loginToBEFinMutation = useMutation(
    () => {
      const returnPath = `${location.pathname}?source=${INTERVAT_GRANT_SOURCE}&workspaceId=${selectedWorkspaceId}`;
      const redirectUrl = getRedirectUrl(returnPath);
      const fullRedirectUrl = `${window.location.origin}${redirectUrl}`;
      return loginToBEFin(fullRedirectUrl);
    },
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(getBEFinSessionQueryKey());
        if (res.authUri) {
          window.location.href = res.authUri;
        }
      },
      onError: () => {
        onClose();
      },
    },
  );
  const { mutate: triggerLoginToBEFin } = loginToBEFinMutation;

  const handleGivePermission = () => {
    if (!selectedWorkspaceId) return;
    logAmplitude(
      WEBAPP_TAXESINTERVATMODAL_CLICKEDGIVEPERMISSIONAFTERWORKSPACESELECTED,
      { entrySource: intervatGrantEntrySource },
    );
    if (canGrantIntervatWithoutRedirect) {
      grantAccessToWorkspace(selectedWorkspaceId);
    } else {
      setStep('redirect');
    }
  };

  const handleContinueToSPF = useCallback(() => {
    triggerLoginToBEFin();
  }, [triggerLoginToBEFin]);

  useEffect(() => {
    if (step !== 'redirect' || !selectedWorkspaceId) return;
    const timeout = window.setTimeout(() => {
      if (canGrantIntervatWithoutRedirectRef.current) {
        grantAccessToWorkspace(selectedWorkspaceId);
        return;
      }
      handleContinueToSPF();
    }, 2500);
    return () => window.clearTimeout(timeout);
  }, [step, selectedWorkspaceId, grantAccessToWorkspace, handleContinueToSPF]);

  const description = canGrantIntervatWithoutRedirect
    ? t('taxes.intervat_grant_modal.description_session_active')
    : t('taxes.intervat_grant_modal.description');

  if (step === 'redirect') {
    return (
      <div
        className="flex flex-col w-full"
        data-testid="intervat-grant-modal-redirect"
      >
        <div className="flex items-center justify-center gap-2">
          <TransferDataIntervatLottie />
        </div>
        <Title type={SizesEnum.h6} className="text-center">
          {t('taxes.intervat_grant_modal.redirect_title')}
        </Title>
        <p className="text-center text-base text-primary-600 mb-6">
          {t('taxes.intervat_grant_modal.redirect_subtitle')}
        </p>
        <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-100 text-primary-700 text-sm mb-6 w-fit mx-auto">
          <InformationCircleIcon className="w-5 h-5 flex-shrink-0" />
          <span>
            {t('taxes.intervat_grant_modal.access_granted_until_revoked')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full" data-testid="intervat-grant-modal">
      <Title type={SizesEnum.h6} className="text-center mb-2">
        {t('taxes.intervat_grant_modal.title')}
      </Title>
      <p className="text-center text-sm text-primary-600 mb-4">{description}</p>
      {canGrantIntervatWithoutRedirect && (
        <div
          className="flex items-center justify-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm mb-4 w-fit mx-auto"
          role="status"
        >
          <InformationCircleIcon className="w-5 h-5 flex-shrink-0" />
          <span>{t('taxes.intervat_grant_modal.already_logged_in')}</span>
        </div>
      )}

      {isLoading ? (
        <div className="py-8 flex flex-col items-center justify-center gap-3 text-primary-500 text-sm">
          <Loader />
        </div>
      ) : (
        <div className="space-y-2 mb-6">
          {accountantWorkspaces.map((workspace, idx) => {
            const name = getAccountantDisplayName(workspace);
            const subline = getWorkspaceSubline(workspace);
            const initial = (name || workspace.name).charAt(0).toUpperCase();
            const isSelected = selectedWorkspaceId === workspace._id;

            return (
              <label
                key={workspace._id}
                className="flex items-start gap-3 p-3 rounded-lg border border-primary-200 cursor-pointer hover:bg-primary-25 transition-colors duration-150 ease-out active:scale-[0.95]"
              >
                <span className="flex-shrink-0 pt-0.5">
                  <RadioButton
                    name="intervat-grant-workspace"
                    color="taxes"
                    checked={isSelected}
                    onChange={() => setSelectedWorkspaceId(workspace._id)}
                  />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-primary-900">
                    {name}
                  </p>
                  <p className="text-xs text-primary-600 truncate">{subline}</p>
                </div>
                <span
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${getInitialColorClass(
                    idx,
                  )}`}
                  aria-hidden
                >
                  {initial}
                </span>
              </label>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-3">
        <Button
          color="taxes"
          structure="primary"
          className="w-full"
          disabled={!selectedWorkspaceId}
          loading={grantAccessToWorkspaceMutation.isLoading}
          onClick={handleGivePermission}
        >
          {t('taxes.intervat_grant_modal.give_permission')}
        </Button>
        <Button
          asDefault
          structure="text"
          className="text-primary-600 text-sm"
          onClick={onClose}
        >
          {t('taxes.intervat_grant_modal.not_now')}
        </Button>
      </div>
    </div>
  );
};

export default IntervatGrantModal;
